import type { IIdDictionary } from "../interfaces/entity";
import type { IHosRuleset } from "../interfaces/hosRuleset";
import { SPLIT_CONFIGS } from "../interfaces/splitConfig";

const SECONDS = 1000;
const MINUTES = 60 * SECONDS;
const HOURS = 60 * MINUTES;
const DAYS = 24 * HOURS;

export const MAX_REPORT_CYCLE_DAYS = 8;

export const RULESETS: IIdDictionary<IHosRuleset> = {
    None: {
        name: "No Ruleset (USA 7-day cycle)",
        config: {
            cycle: {
                cycleDays: 7
            },
            logRetentionDays: 7
        }
    },

    None8Day: {
        name: "No Ruleset (USA 8-day cycle)",
        config: {
            cycle: {
                cycleDays: 8
            },
            logRetentionDays: 7
        }
    },

    NoneCanada: {
        name: "No Ruleset (Canada South of 60°N)",
        config: {
            isCanada: true,
            cycle: {
                cycleDays: 7
            },
            logRetentionDays: 14
        }
    },

    NoneCanadaNorthOf60: {
        name: "No Ruleset (Canada North of 60°N)",
        config: {
            isCanada: true,
            cycle: {
                cycleDays: 7
            },
            logRetentionDays: 14
        }
    },

    America7Day: {
        name: "USA Property 60-hour/7-day",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            logRetentionDays: 7
        }
    },

    America7DaySleeper: {
        name: "USA Property 60-hour/7-day with split sleeper",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    America7DayBig: {
        name: "USA Property 60-hour/7-day (16-hour exemption)",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7
        }
    },

    America7DayBigSleeper: {
        name: "USA Property 60-hour/7-day with split sleeper (16-hour exemption)",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    America8Day: {
        name: "USA Property 70-hour/8-day",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            logRetentionDays: 7
        }
    },

    America8DaySleeper: {
        name: "USA Property 70-hour/8-day with split sleeper",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    America8DayBig: {
        name: "USA Property 70-hour/8-day (16-hour exemption)",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7
        }
    },

    America8DayBigSleeper: {
        name: "USA Property 70-hour/8-day with split sleeper (16-hour exemption)",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    AmericaShortHaul: {
        name: "USA Property Short-haul 60-hour/7-day 12hr Workday",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            exempt: true
        }
    },

    AmericaShortHaulBig: {
        name: "USA Property Short-haul 60-hour/7-day 14hr Workday (16-hour exemption)",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7,
            exempt: true
        }
    },

    AmericaShortHaul8Day: {
        name: "USA Property Short-haul 70-hour/8-day 12hr Workday",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            exempt: true
        }
    },

    AmericaShortHaul8DayBig: {
        name: "USA Property Short-haul 70-hour/8-day 14hr Workday (16-hour exemption)",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7,
            exempt: true
        }
    },

    AmericaShortHaul14hrWorkday: {
        name: "USA Property Short-haul 60-hour/7-day 14hr Workday",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            exempt: true
        }
    },

    AmericaShortHaul8Day14hrWorkday: {
        name: "USA Property Short-haul 70-hour/8-day 14hr Workday",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            exempt: true
        }
    },

    America7DayPassenger: {
        name: "USA Passenger 60-hour/7-day",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS
            },
            workday: {
                driveMaximum: 10 * HOURS,
                dutyMaximum: 15 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 7,
            splitType: "passenger",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_2_8,
            adverseDoesNotExtendWorkday: true
        }
    },

    America8DayPassenger: {
        name: "USA Passenger 70-hour/8-day",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS
            },
            workday: {
                driveMaximum: 10 * HOURS,
                dutyMaximum: 15 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 7,
            splitType: "passenger",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_2_8,
            adverseDoesNotExtendWorkday: true
        }
    },

    AmericaShortHaulPassenger: {
        name: "USA Passenger Short-haul 60-hour/7-day",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS
            },
            workday: {
                driveMaximum: 10 * HOURS,
                dutyMaximum: 14 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 7,
            exempt: true,
            splitType: "passenger",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_2_8,
            adverseDoesNotExtendWorkday: true
        }
    },

    AmericaShortHaulPassenger8Day: {
        name: "USA Passenger Short-haul 70-hour/8-day",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS
            },
            workday: {
                driveMaximum: 10 * HOURS,
                dutyMaximum: 12 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 7,
            exempt: true,
            splitType: "passenger",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_2_8,
            adverseDoesNotExtendWorkday: true
        }
    },

    CanadaBCLoggingTruck: {
        name: "BC Logging Truck",
        config: {
            isCanada: true,
            cycle: {
                cycleDays: 7,
                cycleMaximum: 80 * HOURS,
                cycleDrivingMaximum: 65 * HOURS,
                cycleRestMinimum: 24 * HOURS,
                cycleRestDays: 7
            },
            daily: {
                driveMaximum: 13 * HOURS,
                dutyMaximum: 15 * HOURS,
                deferralAllowed: false
            },
            workday: {
                driveMaximum: 13 * HOURS,
                dutyMaximum: 15 * HOURS,
                workdayMaximum: 15 * HOURS,
                offDutyMinimum: 9 * HOURS
            },
            logRetentionDays: 14,
            customStartOfDay: true,
            adverseDoesNotExtendWorkday: true
        }
    },

    CanadaCycleOne: {
        name: "Canada Cycle 1 (7 day) South of 60°N",
        config: {
            isCanada: true,
            cycle: {
                cycleDays: 7,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 36 * HOURS,
                cycleRestMinimum: 24 * HOURS,
                cycleRestDays: 14
            },
            daily: {
                driveMaximum: 13 * HOURS,
                dutyMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            workday: {
                driveMaximum: 13 * HOURS,
                dutyMaximum: 14 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                dailyRestMinimum: 2 * HOURS
            },
            logRetentionDays: 14,
            splitType: "canada",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_2_10,
            customStartOfDay: true,
            resetPreviousCycle: true,
            adverseDoesNotExtendWorkday: true
        }
    },

    CanadaCycleTwo: {
        name: "Canada Cycle 2 (14 day) South of 60°N",
        config: {
            isCanada: true,
            cycle: {
                cycleDays: 14,
                cycleMaximum: 120 * HOURS,
                cycleRestartMinimum: 72 * HOURS,
                cycleRestMinimum: 24 * HOURS,
                cycleRestDays: 14,
                dutyMaximumSinceCycleRest: 70 * HOURS
            },
            daily: {
                driveMaximum: 13 * HOURS,
                dutyMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            workday: {
                driveMaximum: 13 * HOURS,
                dutyMaximum: 14 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                dailyRestMinimum: 2 * HOURS
            },
            logRetentionDays: 14,
            splitType: "canada",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_2_10,
            customStartOfDay: true,
            resetPreviousCycle: true,
            adverseDoesNotExtendWorkday: true
        }
    },

    CanadaCycleOneTeam: {
        name: "Canada Team Cycle 1 (7 day) South of 60°N",
        config: {
            isCanada: true,
            cycle: {
                cycleDays: 7,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 36 * HOURS,
                cycleRestMinimum: 24 * HOURS,
                cycleRestDays: 14
            },
            daily: {
                driveMaximum: 13 * HOURS,
                dutyMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            workday: {
                driveMaximum: 13 * HOURS,
                dutyMaximum: 14 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                dailyRestMinimum: 2 * HOURS
            },
            logRetentionDays: 14,
            splitType: "canadateam",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_4_8,
            customStartOfDay: true,
            resetPreviousCycle: true,
            adverseDoesNotExtendWorkday: true
        }
    },

    CanadaCycleTwoTeam: {
        name: "Canada Team Cycle 2 (14 day) South of 60°N",
        config: {
            isCanada: true,
            cycle: {
                cycleDays: 14,
                cycleMaximum: 120 * HOURS,
                cycleRestartMinimum: 72 * HOURS,
                cycleRestMinimum: 24 * HOURS,
                cycleRestDays: 14,
                dutyMaximumSinceCycleRest: 70 * HOURS
            },
            daily: {
                driveMaximum: 13 * HOURS,
                dutyMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            workday: {
                driveMaximum: 13 * HOURS,
                dutyMaximum: 14 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                dailyRestMinimum: 2 * HOURS
            },
            logRetentionDays: 14,
            splitType: "canadateam",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_4_8,
            customStartOfDay: true,
            resetPreviousCycle: true,
            adverseDoesNotExtendWorkday: true
        }
    },

    CanadaNorthOf60CycleOne: {
        name: "Canada Cycle 1 (7 day) North of 60°N",
        config: {
            isCanada: true,
            cycle: {
                cycleDays: 7,
                cycleMaximum: 80 * HOURS,
                cycleRestartMinimum: 36 * HOURS,
                cycleRestMinimum: 24 * HOURS,
                cycleRestDays: 14
            },
            workday: {
                driveMaximum: 15 * HOURS,
                dutyMaximum: 18 * HOURS,
                workdayMaximum: 20 * HOURS,
                workdayMaximumWhenSplitSB: 18 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 14,
            splitType: "passenger",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_2_8,
            resetPreviousCycle: true,
            adverseDoesNotExtendWorkday: true
        }
    },

    CanadaNorthOf60CycleTwo: {
        name: "Canada Cycle 2 (14 day) North of 60°N",
        config: {
            isCanada: true,
            cycle: {
                cycleDays: 14,
                cycleMaximum: 120 * HOURS,
                cycleRestartMinimum: 72 * HOURS,
                cycleRestMinimum: 24 * HOURS,
                cycleRestDays: 14,
                dutyMaximumSinceCycleRest: 80 * HOURS
            },
            workday: {
                driveMaximum: 15 * HOURS,
                dutyMaximum: 18 * HOURS,
                workdayMaximum: 20 * HOURS,
                workdayMaximumWhenSplitSB: 18 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 14,
            splitType: "passenger",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_2_8,
            resetPreviousCycle: true,
            adverseDoesNotExtendWorkday: true
        }
    },

    CanadaNorthOf60CycleOneTeam: {
        name: "Canada Team Cycle 1 (7 day) North of 60°N",
        config: {
            isCanada: true,
            cycle: {
                cycleDays: 7,
                cycleMaximum: 80 * HOURS,
                cycleRestartMinimum: 36 * HOURS,
                cycleRestMinimum: 24 * HOURS,
                cycleRestDays: 14
            },
            workday: {
                driveMaximum: 15 * HOURS,
                dutyMaximum: 18 * HOURS,
                workdayMaximum: 20 * HOURS,
                workdayMaximumWhenSplitSB: 18 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 14,
            splitType: "canadanorthteam",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_4_8,
            resetPreviousCycle: true,
            adverseDoesNotExtendWorkday: true
        }
    },

    CanadaNorthOf60CycleTwoTeam: {
        name: "Canada Team Cycle 2 (14 day) North of 60°N",
        config: {
            isCanada: true,
            cycle: {
                cycleDays: 14,
                cycleMaximum: 120 * HOURS,
                cycleRestartMinimum: 72 * HOURS,
                cycleRestMinimum: 24 * HOURS,
                cycleRestDays: 14,
                dutyMaximumSinceCycleRest: 80 * HOURS
            },
            workday: {
                driveMaximum: 15 * HOURS,
                dutyMaximum: 18 * HOURS,
                workdayMaximum: 20 * HOURS,
                workdayMaximumWhenSplitSB: 18 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 14,
            splitType: "canadanorthteam",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_4_8,
            resetPreviousCycle: true,
            adverseDoesNotExtendWorkday: true
        }
    },

    CaliforniaProperty: {
        name: "California Property Intrastate",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 80 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            splitType: "californiaProperty",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_CALIFORNIA
        }
    },

    CaliforniaPropertyShortHaul: {
        name: "California Property Short-haul",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 80 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 12 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            splitType: "californiaProperty",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_CALIFORNIA
        }
    },

    CaliforniaPassenger: {
        name: "California Passenger Intrastate",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 80 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 10 * HOURS,
                dutyMaximum: 15 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 7,
            splitType: "passenger",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_2_8
        }
    },

    CaliforniaFlammableLiquid: {
        name: "California Flammable Liquid",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 80 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 10 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            splitType: "californiaProperty",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_CALIFORNIA
        }
    },

    CaliforniaFlammableLiquidWithRestRequirement: {
        name: "California Flammable Liquid with rest requirement",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 80 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 10 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 5 * HOURS
            },
            logRetentionDays: 7,
            splitType: "californiaProperty",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_CALIFORNIA
        }
    },

    CaliforniaSchoolPupil: {
        name: "California School Pupil",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 80 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 10 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 7
        }
    },

    CaliforniaSchoolPupilWithRestRequirement: {
        name: "California School Pupil with rest requirement",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 80 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 10 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 5 * HOURS
            },
            logRetentionDays: 7
        }
    },

    CaliforniaFarmProduct: {
        name: "California Farm Product",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 112 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                dutyMaximum: 16 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 7
        }
    },

    CaliforniaFarmProductWithRestRequirement: {
        name: "California Farm Product with rest requirement",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 112 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                dutyMaximum: 16 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 5 * HOURS
            },
            logRetentionDays: 7
        }
    },

    OilTransport7Day: {
        name: "USA Property 60-hour/7-day 24-hour restart",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            logRetentionDays: 7
        }
    },

    OilTransport7DaySleeper: {
        name: "USA Property 60-hour/7-day with split sleeper and 24-hour restart",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    OilTransport7DayBig: {
        name: "USA Property 60-hour/7-day 24-hour restart (16-hour exemption)",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7
        }
    },

    OilTransport7DayBigSleeper: {
        name: "USA Property 60-hour/7-day with split sleeper and 24-hour restart (16-hour exemption)",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    OilTransport8Day: {
        name: "USA Property 70-hour/8-day 24-hour restart",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            logRetentionDays: 7
        }
    },

    OilTransport8DaySleeper: {
        name: "USA Property 70-hour/8-day with split sleeper and 24-hour restart",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    OilTransport8DayBig: {
        name: "USA Property 70-hour/8-day 24-hour restart (16-hour exemption)",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7
        }
    },

    OilTransport8DayBigSleeper: {
        name: "USA Property 70-hour/8-day with split sleeper and 24-hour restart (16-hour exemption)",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    OilTransportShortHaul: {
        name: "USA Property Short-haul 60-hour/7-day 12hr Workday 24-hour restart",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            exempt: true
        }
    },

    OilTransportShortHaulBig: {
        name: "USA Property Short-haul 60-hour/7-day 14hr Workday 24-hour restart (16-hour exemption)",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7,
            exempt: true
        }
    },

    OilTransportShortHaul8Day: {
        name: "USA Property Short-haul 70-hour/8-day 12hr Workday 24-hour restart",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            exempt: true
        }
    },

    OilTransportShortHaul8DayBig: {
        name: "USA Property Short-haul 70-hour/8-day 14hr Workday 24-hour restart (16-hour exemption)",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7,
            exempt: true
        }
    },

    OilTransportShortHaul14hrWorkday: {
        name: "USA Property Short-haul 60-hour/7-day 14hr Workday 24-hour restart",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            exempt: true
        }
    },

    OilTransportShortHaul8Day14hrWorkday: {
        name: "USA Property Short-haul 70-hour/8-day 14hr Workday 24-hour restart",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            exempt: true
        }
    },

    OilTransportCalifornia8day: {
        name: "California Oil Transport Property Intrastate with rest requirement",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 80 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 5 * HOURS
            },
            logRetentionDays: 7,
            splitType: "californiaProperty",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_CALIFORNIA
        }
    },

    OilTransportCaliforniaProperty: {
        name: "California Oil Transport Property Intrastate",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 80 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            splitType: "californiaProperty",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_CALIFORNIA
        }
    },

    AmericaSalesperson: {
        name: "USA Salesperson",
        config: {
            cycle: {
                cycleDays: 7,
                cycleDrivingMaximum: 40 * HOURS,
                cycleMaximum: 40 * HOURS,
                cycleStatuses: ["D"],
                cycleRestartMinimum: 34 * HOURS,
                maxPercentDriving: 0.5
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            exempt: true
        }
    },

    AmericaSalespersonNonCdlShortHaul: {
        name: "USA Salesperson Non-CDL Short Haul",
        config: {
            cycle: {
                cycleDays: 7,
                cycleDrivingMaximum: 40 * HOURS,
                cycleMaximum: 40 * HOURS,
                cycleStatuses: ["D"],
                cycleRestartMinimum: 34 * HOURS,
                maxPercentDriving: 0.5
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            bigDayMaxPerCycle: 2,
            bigDayBonus: 2 * HOURS,
            bigDayIgnoresCycleReset: true,
            exempt: true
        }
    },

    OilWell7Day: {
        name: "USA Oil Well 60-hour/7-day",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            oilwell: true,
            logRetentionDays: 7
        }
    },

    OilWell7DaySleeper: {
        name: "USA Oil Well 60-hour/7-day with split sleeper",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            oilwell: true,
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    OilWell7DayBig: {
        name: "USA Oil Well 60-hour/7-day (16-hour exemption)",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            bigDayBonus: 2 * HOURS,
            oilwell: true,
            logRetentionDays: 7
        }
    },

    OilWell7DayBigSleeper: {
        name: "USA Oil Well 60-hour/7-day (16-hour exemption) with split sleeper",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            bigDayBonus: 2 * HOURS,
            oilwell: true,
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    OilWell8Day: {
        name: "USA Oil Well 70-hour/8-day",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            oilwell: true,
            logRetentionDays: 7
        }
    },

    OilWell8DaySleeper: {
        name: "USA Oil Well 70-hour/8-day with split sleeper",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            oilwell: true,
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    OilWell8DayBig: {
        name: "USA Oil Well 70-hour/8-day (16-hour exemption)",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            bigDayBonus: 2 * HOURS,
            oilwell: true,
            logRetentionDays: 7
        }
    },

    OilWell8DayBigSleeper: {
        name: "USA Oil Well 70-hour/8-day (16-hour exemption) with split sleeper",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            bigDayBonus: 2 * HOURS,
            oilwell: true,
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    OilWellCalifornia8day: {
        name: "California Oil Well Property Intrastate with rest requirement",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 80 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 5 * HOURS
            },
            oilwell: true,
            logRetentionDays: 7,
            splitType: "californiaProperty",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_CALIFORNIA
        }
    },

    OilWellCaliforniaProperty: {
        name: "California Oil Well Property Intrastate",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 80 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            oilwell: true,
            logRetentionDays: 7,
            splitType: "californiaProperty",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_CALIFORNIA
        }
    },

    AmericaTexas: {
        name: "Texas Intrastate",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                dutyMaximum: 15 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 7,
            splitType: "passenger",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_2_8
        }
    },

    AmericaTexasShortHaul: {
        name: "Texas Short-Haul 70-hour/7-day Intrastate",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                dutyMaximum: 15 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 7,
            exempt: true,
            splitType: "passenger",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_2_8
        }
    },

    AmericaTexasShortHaul8Day: {
        name: "Texas Short-Haul 70-hour/7-day Intrastate",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                dutyMaximum: 15 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 7,
            exempt: true,
            splitType: "passenger",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_2_8
        }
    },

    OilTransportTexas: {
        name: "USA Texas Oil Transport 70-hour/7-day",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                dutyMaximum: 15 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 7,
            splitType: "passenger",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_2_8
        }
    },

    OilWellTexas: {
        name: "USA Texas Oil Well 70-hour/7-day",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                dutyMaximum: 15 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            oilwell: true,
            logRetentionDays: 7,
            splitType: "passenger",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_2_8
        }
    },

    Florida7Day: {
        name: "USA Florida 70-hour/7-Day",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    Florida8Day: {
        name: "USA Florida 80-hour/8-Day",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 80 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    FloridaShortHaul7Day: {
        name: "USA Florida Short-haul 70-hour/7-Day",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 12 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            exempt: true,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    FloridaShortHaul8Day: {
        name: "USA Florida Short-haul 80-hour/8-Day",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 80 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 12 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            exempt: true,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    America7DayNo34h: {
        name: "USA Property 60-hour/7-day without 34-hour restart",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            logRetentionDays: 7
        }
    },

    America7DayNo34hSleeper: {
        name: "USA Property 60-hour/7-day without 34-hour restart with split sleeper",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    America8DayNo34h: {
        name: "USA Property 70-hour/8-day without 34-hour restart",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            logRetentionDays: 7
        }
    },

    America8DayNo34hSleeper: {
        name: "USA Property 70-hour/8-day without 34-hour restart with split sleeper",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    AmericaShortHaulNo34h: {
        name: "USA Property Short-haul 60-hour/7-day without 34-hour restart",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            exempt: true
        }
    },

    AmericaShortHaulNo34hBig: {
        name: "USA Property Short-haul 60-hour/7-day without 34-hour restart (16-hour exemption)",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7,
            exempt: true
        }
    },

    AmericaShortHaul8DayNo34h: {
        name: "USA Property Short-haul 70-hour/8-day without 34-hour restart",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            exempt: true
        }
    },

    AmericaShortHaul8DayNo34hBig: {
        name: "USA Property Short-haul 70-hour/8-day without 34-hour restart (16-hour exemption)",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7,
            exempt: true
        }
    },

    BrazilShortHaul: {
        name: "Brazil Property Short-haul",
        config: {
            cycle: {
                cycleDays: 7
            },
            workday: {
                dutyMaximum: 8 * HOURS,
                offDutyMinimum: 11 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 5.5 * HOURS
            },
            logRetentionDays: 7,
            exempt: true
        }
    },

    CarrierExemption: {
        name: "Carrier Exemption",
        config: {
            logRetentionDays: 14,
            exempt: true
        }
    },

    AmericaNonCdlShortHaul7Day: {
        name: "Non-CDL Short-haul 60-hour/7-day",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            bigDayMaxPerCycle: 2,
            bigDayBonus: 2 * HOURS,
            bigDayIgnoresCycleReset: true
        }
    },

    AmericaNonCdlShortHaul8Day: {
        name: "Non-CDL Short-haul 70-hour/8-day",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            bigDayMaxPerCycle: 2,
            bigDayBonus: 2 * HOURS,
            bigDayIgnoresCycleReset: true
        }
    },

    WashingtonIntrastate7Day: {
        name: "Washington Intrastate Logging/Dumptruck 80-hour/7-day",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 80 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            bigDayMaxPerCycle: 2,
            bigDayBonus: 2 * HOURS,
            bigDayIgnoresCycleReset: true
        }
    },

    WashingtonIntrastate8Day: {
        name: "Washington Intrastate Logging/Dumptruck 90-hour/8-day",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 90 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            bigDayMaxPerCycle: 2,
            bigDayBonus: 2 * HOURS,
            bigDayIgnoresCycleReset: true
        }
    },

    AmericaNoRestRequirement7Day: {
        name: "USA Property 60-hour/7-day without rest requirement",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7
        }
    },

    AmericaNoRestRequirement7DaySleeper: {
        name: "USA Property 60-hour/7-day without rest requirement with split sleeper",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    AmericaNoRestRequirement7DayBig: {
        name: "USA Property 60-hour/7-day (16-hour exemption) without rest requirement",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7
        }
    },

    AmericaNoRestRequirement7DayBigSleeper: {
        name: "USA Property 60-hour/7-day without rest requirement with split sleeper (16-hour exemption)",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    AmericaNoRestRequirement8Day: {
        name: "USA Property 70-hour/8-day without rest requirement",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7
        }
    },

    AmericaNoRestRequirement8DaySleeper: {
        name: "USA Property 70-hour/8-day without rest requirement with split sleeper",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    AmericaNoRestRequirement8DayBig: {
        name: "USA Property 70-hour/8-day (16-hour exemption) without rest requirement",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7
        }
    },

    AmericaNoRestRequirement8DayBigSleeper: {
        name: "USA Property 70-hour/8-day without rest requirement with split sleeper (16-hour exemption)",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    OilTransportNoRestRequirement7Day: {
        name: "USA Oil Transport 60-hour/7-day without rest requirement",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7
        }
    },

    OilTransportNoRestRequirement7DaySleeper: {
        name: "USA Oil Transport 60-hour/7-day without rest requirement with split sleeper",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    OilTransportNoRestRequirement7DayBig: {
        name: "USA Oil Transport 60-hour/7-day (16-hour exemption) without rest requirement",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7
        }
    },

    OilTransportNoRestRequirement7DayBigSleeper: {
        name: "USA Oil Transport 60-hour/7-day (16-hour exemption) without rest requirement with split sleeper",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    OilTransportNoRestRequirement8Day: {
        name: "USA Oil Transport 70-hour/8-day without rest requirement",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7
        }
    },

    OilTransportNoRestRequirement8DaySleeper: {
        name: "USA Oil Transport 70-hour/8-day without rest requirement with split sleeper",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    OilTransportNoRestRequirement8DayBig: {
        name: "USA Oil Transport 70-hour/8-day (16-hour exemption) without rest requirement",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7
        }
    },

    OilTransportNoRestRequirement8DayBigSleeper: {
        name: "USA Oil Transport 70-hour/8-day (16-hour exemption) without rest requirement with split sleeper",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    OilWellNoRestRequirement7Day: {
        name: "USA Oil Well 60-hour/7-day without rest requirement",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            oilwell: true,
            logRetentionDays: 7
        }
    },

    OilWellNoRestRequirement7DaySleeper: {
        name: "USA Oil Well 60-hour/7-day without rest requirement with split sleeper",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            oilwell: true,
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    OilWellNoRestRequirement7DayBig: {
        name: "USA Oil Well 60-hour/7-day (16-hour exemption) without rest requirement",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            oilwell: true,
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7
        }
    },

    OilWellNoRestRequirement7DayBigSleeper: {
        name: "USA Oil Well 60-hour/7-day (16-hour exemption) without rest requirement with split sleeper",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            oilwell: true,
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    OilWellNoRestRequirement8Day: {
        name: "USA Oil Well 70-hour/8-day without rest requirement",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            oilwell: true,
            logRetentionDays: 7
        }
    },

    OilWellNoRestRequirement8DaySleeper: {
        name: "USA Oil Well 70-hour/8-day without rest requirement with split sleeper",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            oilwell: true,
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    OilWellNoRestRequirement8DayBig: {
        name: "USA Oil Well 70-hour/8-day (16-hour exemption) without rest requirement",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            oilwell: true,
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7
        }
    },

    OilWellNoRestRequirement8DayBigSleeper: {
        name: "USA Oil Well 70-hour/8-day (16-hour exemption) without rest requirement with split sleeper",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 24 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            oilwell: true,
            bigDayBonus: 2 * HOURS,
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    NorthDakota7Day: {
        name: "North Dakota 70-hour/7-day",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 70 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 15 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 7
        }
    },

    SouthCarolina7Day: {
        name: "South Carolina 70-hour/7-day",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 70 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 7
        }
    },

    SouthCarolina8Day: {
        name: "South Carolina 80-hour/8-day",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 80 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 7
        }
    },

    MarylandShortHaul7Day: {
        name: "Maryland Short Haul 70-hour/7-day",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 70 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 7
        }
    },

    MarylandShortHaul8Day: {
        name: "Maryland Short Haul 80-hour/8-day",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 80 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 7
        }
    },

    NorthDakotaShortHaul7Day: {
        name: "North Dakota Short Haul 70-hour/7-day",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 70 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 12 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 7
        }
    },

    AlaskaPassenger7Day: {
        name: "Alaska Passenger 70-hour/7-day",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 70 * HOURS
            },
            workday: {
                driveMaximum: 15 * HOURS,
                dutyMaximum: 20 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 7
        }
    },

    AlaskaPassenger8Day: {
        name: "Alaska Passenger 80-hour/8-day",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 80 * HOURS
            },
            workday: {
                driveMaximum: 15 * HOURS,
                dutyMaximum: 20 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            logRetentionDays: 7
        }
    },

    AlaskaProperty7Day: {
        name: "Alaska Property 70-hour/7-day",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 70 * HOURS
            },
            workday: {
                driveMaximum: 15 * HOURS,
                workdayMaximum: 20 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7
        }
    },

    AlaskaProperty7DaySleeper: {
        name: "Alaska Property 70-hour/7-day with split sleeper",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 70 * HOURS
            },
            workday: {
                driveMaximum: 15 * HOURS,
                workdayMaximum: 20 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    AlaskaProperty8Day: {
        name: "Alaska Property 80-hour/8-day",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 80 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 15 * HOURS,
                workdayMaximum: 20 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7
        }
    },

    AlaskaProperty8DaySleeper: {
        name: "Alaska Property 80-hour/8-day with split sleeper",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 80 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 15 * HOURS,
                workdayMaximum: 20 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            logRetentionDays: 7,
            splitType: "property",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_7_2
        }
    },

    Nebraska7day: {
        name: "Nebraska 70-hour/7-day",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            logRetentionDays: 7
        }
    },

    Nebraska8day: {
        name: "Nebraska 80-hour/8-day",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 80 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 8 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            logRetentionDays: 7
        }
    },

    California8day: {
        name: "California Property Intrastate with rest requirement",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 80 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 5 * HOURS
            },
            logRetentionDays: 7,
            splitType: "californiaProperty",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_CALIFORNIA
        }
    },

    CaliforniaPropertyShortHaulWithRest: {
        name: "California Property Short-haul with rest requirement",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 80 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 12 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 5 * HOURS
            },
            logRetentionDays: 7,
            splitType: "californiaProperty",
            splitConfig: SPLIT_CONFIGS.ASYMMETRIC_CALIFORNIA
        }
    },

    Oregon7day: {
        name: "Oregon 70-hour/7-day",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            logRetentionDays: 7
        }
    },

    Oregon8day: {
        name: "Oregon 80-hour/8-day",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 80 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            logRetentionDays: 7
        }
    },

    CanadaOil: {
        name: "Canada South of 60°N Oil Permit",
        config: {
            isCanada: true,
            cycle: {
                cycleDays: 24,
                cycleRestartMinimum: 72 * HOURS,
                cycleRestMinimum: 24 * HOURS,
                cycleRestPeriods: 3,
                cycleRestDays: 23
            },
            daily: {
                driveMaximum: 13 * HOURS,
                dutyMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS,
                deferralAllowed: false
            },
            workday: {
                driveMaximum: 13 * HOURS,
                dutyMaximum: 14 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 8 * HOURS,
                wtExtendsWorkday: false,
                consecutiveOffCountIncludesWt: false
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                dailyRestMinimum: 2 * HOURS
            },
            logRetentionDays: 24,
            splitType: "canada",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_2_10,
            customStartOfDay: true,
            adverseDoesNotExtendWorkday: true,
            oilwell: true
        }
    },

    CanadaNorthOf60Oil: {
        name: "Canada North of 60°N Oil Permit",
        config: {
            isCanada: true,
            cycle: {
                cycleDays: 24,
                cycleRestartMinimum: 72 * HOURS,
                cycleRestMinimum: 24 * HOURS,
                cycleRestPeriods: 3,
                cycleRestDays: 23
            },
            daily: {
                deferralAllowed: false
            },
            workday: {
                driveMaximum: 15 * HOURS,
                dutyMaximum: 18 * HOURS,
                workdayMaximum: 20 * HOURS,
                offDutyMinimum: 8 * HOURS,
                wtExtendsWorkday: false,
                consecutiveOffCountIncludesWt: false
            },
            logRetentionDays: 24,
            splitType: "passenger",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_2_8,
            adverseDoesNotExtendWorkday: true,
            oilwell: true
        }
    },

    CanadaOilTeam: {
        name: "Canada South of 60°N Oil Permit Team",
        config: {
            isCanada: true,
            cycle: {
                cycleDays: 24,
                cycleRestartMinimum: 72 * HOURS,
                cycleRestMinimum: 24 * HOURS,
                cycleRestPeriods: 3,
                cycleRestDays: 23
            },
            daily: {
                driveMaximum: 13 * HOURS,
                dutyMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS,
                deferralAllowed: false
            },
            workday: {
                driveMaximum: 13 * HOURS,
                dutyMaximum: 14 * HOURS,
                workdayMaximum: 16 * HOURS,
                offDutyMinimum: 8 * HOURS,
                wtExtendsWorkday: false,
                consecutiveOffCountIncludesWt: false
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                dailyRestMinimum: 2 * HOURS
            },
            logRetentionDays: 24,
            splitType: "canadateam",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_4_8,
            customStartOfDay: true,
            adverseDoesNotExtendWorkday: true,
            oilwell: true
        }
    },

    CanadaNorthOf60OilTeam: {
        name: "Canada North of 60°N Oil Permit Team",
        config: {
            isCanada: true,
            cycle: {
                cycleDays: 24,
                cycleRestartMinimum: 72 * HOURS,
                cycleRestMinimum: 24 * HOURS,
                cycleRestPeriods: 3,
                cycleRestDays: 23
            },
            daily: {
                deferralAllowed: false
            },
            workday: {
                driveMaximum: 15 * HOURS,
                dutyMaximum: 18 * HOURS,
                workdayMaximum: 20 * HOURS,
                offDutyMinimum: 8 * HOURS,
                wtExtendsWorkday: false,
                consecutiveOffCountIncludesWt: false
            },
            logRetentionDays: 24,
            splitType: "canadateam",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_4_8,
            adverseDoesNotExtendWorkday: true,
            oilwell: true
        }
    },

    America7DayRailroad: {
        name: "USA Railroad 60-hour/7-day",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            logRetentionDays: 7,
            splitType: "noSplit",
            railroadExempt: true
        }
    },

    America8DayRailroad: {
        name: "USA Railroad 70-hour/8-day",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 11 * HOURS,
                workdayMaximum: 14 * HOURS,
                offDutyMinimum: 10 * HOURS
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 8 * HOURS,
                onDutyCountTowardRest: true
            },
            logRetentionDays: 7,
            splitType: "noSplit",
            railroadExempt: true
        }
    },
    Alberta: {
        name: "Alberta",
        config: {
            isCanada: true,
            workday: {
                driveMaximum: 13 * HOURS,
                dutyMaximum: 15 * HOURS,
                offDutyMinimum: 8 * HOURS,
                exemptionReducedRest: {
                    daysBetweenUse: 7,
                    offDutyMinimum: 4 * HOURS
                }
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS,
                betweenRestLimit: 6 * HOURS,
                earlyRestBreaks: [
                    {
                        restBreakMinimum: 10 * MINUTES,
                        betweenRestLimit: 4 * HOURS
                    }
                ],
                onDutyCountTowardRest: true
            },
            splitType: "passenger",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_2_8,
            logRetentionDays: 14,
            adverseDoesNotExtendWorkday: true
        }
    },
    MalaysiaDrivingHours: {
        name: "Malaysia Driving Hours",
        config: {
            cycle: {
                cycleRestMinimum: 24 * HOURS,
                cycleRestDays: 6 // 5. for every 6 working days, 1 rest day is compulsory;
            },
            workday: {
                workdayMaximum: 12 * HOURS, // 3. total working hours for a day is 12 hours (maximum);
                driveMaximum: 8 * HOURS, // 2. total driving hours for a day is 8 hours (maximum);
                offDutyMinimum: 12 * HOURS // 6. a minimum of 12 hours’ rest before the start of the first journey.
            },
            rest: {
                restBreakMinimum: 0.5 * HOURS, // 4. for every four hours of driving, 30 minutes of rest is compulsory;
                betweenRestLimit: 4 * HOURS
            },
            logRetentionDays: 14,
            adverseDoesNotExtendWorkday: true
        }
    },
    AustraliaStandardHoursSoloExemptionHours: {
        name: "Australia Standard Hours Solo with Exemption Hours",
        config: {
            logRetentionDays: MAX_REPORT_CYCLE_DAYS,
            workRestConfig: [
                {
                    timePeriod: 5.5 * HOURS,
                    restPeriod: {
                        restMinimum: 15 * MINUTES,
                        countRequired: 1
                    },
                    workPeriod: {
                        workMaximum: 5.25 * HOURS
                    }
                },
                {
                    timePeriod: 8 * HOURS,
                    restPeriod: {
                        restMinimum: 15 * MINUTES,
                        countRequired: 2
                    },
                    workPeriod: {
                        workMaximum: 7.5 * HOURS
                    }
                },
                {
                    timePeriod: 11 * HOURS,
                    restPeriod: {
                        restMinimum: 15 * MINUTES,
                        countRequired: 4
                    },
                    workPeriod: {
                        workMaximum: 10 * HOURS
                    }
                },
                {
                    timePeriod: 1 * DAYS,
                    restPeriod: {
                        restMinimum: 7 * HOURS,
                        countRequired: 1
                    },
                    workPeriod: {
                        workMaximum: 12 * HOURS
                    }
                },
                {
                    timePeriod: 7 * DAYS,
                    restPeriod: {
                        restMinimum: 24 * HOURS,
                        countRequired: 1
                    },
                    workPeriod: {
                        workMaximum: 72 * HOURS
                    }
                },
                {
                    timePeriod: 14 * DAYS,
                    restPeriod: {
                        nightRestBreakCount: 4,
                        consecutiveNightRestBreakCount: 2
                    },
                    workPeriod: {
                        workMaximum: 144 * HOURS
                    }
                }
            ],
            exempt: true
        }
    }
};

const LEGACY_TEXAS_SHORT_HAUL_RULESETS: IIdDictionary<IHosRuleset> = {
    AmericaTexasShortHaul: {
        name: "Texas Short-haul 60-hour/7-day Intrastate",
        config: {
            cycle: {
                cycleDays: 7,
                cycleMaximum: 60 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                offDutyMinimum: 8 * HOURS,
                workdayMaximum: 14 * HOURS
            },
            logRetentionDays: 7,
            exempt: true,
            splitType: "passenger",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_2_8
        }
    },
    AmericaTexasShortHaul8Day: {
        name: "Texas Short-haul 70-hour/8-day Intrastate",
        config: {
            cycle: {
                cycleDays: 8,
                cycleMaximum: 70 * HOURS,
                cycleRestartMinimum: 34 * HOURS
            },
            workday: {
                driveMaximum: 12 * HOURS,
                offDutyMinimum: 8 * HOURS,
                workdayMaximum: 14 * HOURS
            },
            logRetentionDays: 7,
            exempt: true,
            splitType: "passenger",
            splitConfig: SPLIT_CONFIGS.SYMMETRIC_2_8
        }
    }
};

const LEGACY_TEXAS_SHORT_HAUL_RULESETS_BY_FLAG: IIdDictionary<IHosRuleset> = {
    ...RULESETS,
    ...LEGACY_TEXAS_SHORT_HAUL_RULESETS
};

export function getEffectiveRuleSets(useLegacyTexasShortHaulRulesets = false): IIdDictionary<IHosRuleset> {
    return useLegacyTexasShortHaulRulesets ? LEGACY_TEXAS_SHORT_HAUL_RULESETS_BY_FLAG : RULESETS;
}

export function getRuleSetForCalculation(ruleSetIndex: string, useLegacyTexasShortHaulRulesets = false): IHosRuleset | undefined {
    return getEffectiveRuleSets(useLegacyTexasShortHaulRulesets)[ruleSetIndex];
}
